import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MessageCircle, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Props {
  leadOrigemId?: string | null;
  telefone?: string | null;
  /** CNPJ do cliente — a conversa carrega `cliente_cnpj` desde a resolução de cliente */
  cnpj?: string | null;
  /** Outros telefones do pedido/lead: o cliente costuma responder de um número diferente do cadastrado */
  telefonesAlternativos?: (string | null | undefined)[];
}

/** Só dígitos, e sem o 55 do país — o que basta para casar por sufixo */
function nucleoTelefone(valor?: string | null): string | null {
  const d = String(valor || '').replace(/\D/g, '');
  if (d.length < 8) return null;
  const semPais = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
  return semPais.length >= 8 ? semPais : null;
}

/**
 * Consultar `whatsapp_conversations` pelo generic `Database` estoura a
 * profundidade do compilador e contamina o arquivo inteiro (TS2589/TS2769) — era
 * por isso que o código original carregava `as any` em cada leitura. Aqui a
 * forma da consulta é declarada explicitamente uma vez: sem `any`, sem o tipo
 * gigante, e os quatro caminhos de busca ficam limpos.
 */
type ConsultaConversa = {
  eq: (coluna: string, valor: string) => ConsultaConversa;
  or: (expressao: string) => ConsultaConversa;
  order: (coluna: string, opcoes: { ascending: boolean; nullsFirst: boolean }) => ConsultaConversa;
  limit: (quantidade: number) => ConsultaConversa;
  maybeSingle: () => Promise<{ data: { id?: string | null } | null }>;
};

const conversas = supabase as unknown as {
  from: (tabela: string) => { select: (colunas: string) => ConsultaConversa };
};

/** Devolve o id da conversa mais recente que casar com o filtro, ou null */
async function primeiraConversa(
  aplicarFiltro: (q: ConsultaConversa) => ConsultaConversa,
): Promise<string | null> {
  const { data } = await aplicarFiltro(conversas.from('whatsapp_conversations').select('id'))
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();
  return data?.id ?? null;
}

/**
 * Abre a conversa do WhatsApp (Evolution / Inbox) do cliente direto da tabela.
 *
 * A busca vai do mais preciso ao mais amplo — lead → CNPJ → cada telefone do
 * pedido → a conversa da tabulação (`leads_carteira.url_conversa_bee`). Antes
 * ela parava no lead + UM telefone, e por isso não achava a conversa quando o
 * cliente respondia de outro número (o pedido tem três campos de telefone),
 * quando a conversa foi ligada ao CNPJ e não ao lead, ou quando a mensagem saiu
 * para um número que não é o do cadastro — foi o caso do pedido 2608040041 em
 * 05/08, onde a conversa registrada na tabulação existia e o botão não achava.
 * Sem conversa nenhuma, abre o Inbox e diz o que foi procurado — assim o
 * consultor sabe que a conversa não existe, em vez de achar que o botão quebrou.
 */
export function BotaoConversaWhats({ leadOrigemId, telefone, cnpj, telefonesAlternativos }: Props) {
  const [loading, setLoading] = useState(false);

  const abrir = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      let convId: string | null = null;

      if (leadOrigemId) {
        convId = await primeiraConversa((q) => q.eq('lead_id', leadOrigemId));
      }

      const cnpjDigits = String(cnpj || '').replace(/\D/g, '');
      if (!convId && cnpjDigits.length === 14) {
        convId = await primeiraConversa((q) => q.eq('cliente_cnpj', cnpjDigits));
      }

      const nucleos = Array.from(
        new Set([telefone, ...(telefonesAlternativos || [])].map(nucleoTelefone).filter(Boolean) as string[]),
      );
      for (const tel of nucleos) {
        if (convId) break;
        convId = await primeiraConversa((q) =>
          q.or(`telefone_canonico.ilike.%${tel}%,telefone.ilike.%${tel}%`),
        );
      }

      // Último recurso: a conversa que o consultor usou ao tabular o lead. O CRM
      // guarda ela em leads_carteira.url_conversa_bee a cada mensagem enviada
      // pela tela do lead — é o "com qual ele tabulou".
      if (!convId && leadOrigemId) {
        const { data } = await supabase
          .from('leads_carteira')
          .select('url_conversa_bee')
          .eq('id', leadOrigemId)
          .maybeSingle();
        const url = data?.url_conversa_bee;
        const achado = url?.match(/conversation=([0-9a-f-]{36})/i)?.[1];
        if (achado) convId = achado;
      }

      if (convId) {
        window.open(`/whatsapp-inbox?conversation=${convId}`, '_blank');
      } else {
        const oQueProcurou = [
          leadOrigemId ? 'lead' : null,
          cnpjDigits.length === 14 ? 'CNPJ' : null,
          nucleos.length ? `${nucleos.length} telefone${nucleos.length > 1 ? 's' : ''}` : null,
        ].filter(Boolean).join(', ');
        toast.info(
          oQueProcurou
            ? `Nenhuma conversa no WhatsApp para este cliente (procurei por ${oQueProcurou}) — abrindo o Inbox.`
            : 'Sem conversa no WhatsApp pra este lead — abrindo o Inbox.',
        );
        window.open('/whatsapp-inbox', '_blank');
      }
    } catch {
      window.open('/whatsapp-inbox', '_blank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={abrir}
            disabled={loading}
            title="Conversa no WhatsApp"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Abrir conversa no WhatsApp (Inbox)</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
